import React from "react";
import { useState, useEffect } from "react";
import { ActionButton, Icon, Text, InputFieldV2, InputRadio } from "../custom-components";
import { IcClose, IcWarningColored } from "../custom-components/Icon";
import Select from "react-select";
import "../styles/purposeContainer.css";
import "../styles/masterSetup.css";
import {
  getProcessingActivity,
  getPurposeList,
} from "../store/actions/CommonAction";
import CustomToast from "./CustomToastContainer";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import "../styles/toast.css";
import { Slide, ToastContainer, toast } from "react-toastify";

const PurposeContainer = ({
  data,
  onChange,
  purposeNumber,
  onClear,
  isFirst,
  processingActivityList,
  setProcessingActivityList,
  purposeOptions,
  setPurposeOptions,
  processingActivityOption,
  setProcessingActivityOptions,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [showNoActivityModal, setShowNoActivityModal] = useState(false);
  const customStyles = {
    control: (base, state) => ({
      ...base,
      color: "#333",
      backgroundColor: "#fff",
      border: "1px solid #ccc",
      borderRadius: "8px",
      padding: "2px 4px",
      fontSize: "14px",
      minHeight: "40px",
      boxShadow: "none",
      borderColor: state.isFocused ? "#ccc" : "#ccc",
      "&:hover": {
        borderColor: "#999",
      },
    }),

    valueContainer: (base) => ({
      ...base,
      padding: "0 8px",
      gap: "4px",
    }),

    placeholder: (base) => ({
      ...base,
      color: "#666",
      fontSize: "14px",
    }),

    multiValue: (base) => ({
      ...base,
      borderRadius: "6px",
      backgroundColor: "#f0f0f0",
      padding: "2px 6px",
    }),

    multiValueLabel: (base) => ({
      ...base,
      color: "#333",
      fontSize: "13px",
    }),

    multiValueRemove: (base) => ({
      ...base,
      color: "#666",
      cursor: "pointer",
      ":hover": {
        backgroundColor: "#e0e0e0",
        color: "#000",
      },
    }),

    menu: (base) => ({
      ...base,
      borderRadius: "8px",
      border: "1px solid #ccc",
      marginTop: "4px",
      zIndex: 9999,
    }),

    menuList: (base) => ({
      ...base,
      padding: "4px 0",
    }),

    option: (base, state) => ({
      ...base,
      fontSize: "14px",
      padding: "8px 12px",
      cursor: "pointer",
      backgroundColor: state.isSelected
        ? "#e6f0ff"
        : state.isFocused
        ? "#f5f5f5"
        : "#fff",
      color: "#333",
      ":active": {
        backgroundColor: "#e6f0ff",
      },
    }),

    dropdownIndicator: (base) => ({
      ...base,
      color: "#666",
      padding: "0 8px",
      "&:hover": {
        color: "#333",
      },
    }),

    clearIndicator: (base) => ({
      ...base,
      padding: "0 8px",
    }),
  };

  const fetchPurposes = async () => {
    try {
      let res = await dispatch(getPurposeList());
      if (res?.data?.searchList) {
        // setPurposeList(res.data.searchList);

        const opts = res.data.searchList.map((item) => ({
          value: item.purposeId,
          label: item.purposeName,
        }));
        setPurposeOptions(opts);
      }
      if (res?.status == 403) {
        toast.error(
          (props) => (
            <CustomToast
              {...props}
              type="error"
              message={"Facing Network Error..Please try again later."}
            />
          ),
          { icon: false }
        );
      }
    } catch (err) {
      if (err[0].errorCode == "JCMP4003" || err[0].errorCode == "JCMP4001") {
        toast.error(
          (props) => (
            <CustomToast {...props} type="error" message={"Session expired"} />
          ),
          { icon: false }
        );
      } else {
        toast.error(
          (props) => (
            <CustomToast
              {...props}
              type="error"
              message={"Error in fetching purpose."}
            />
          ),
          { icon: false }
        );
      }
      console.log("Error fetching purposes:", err);
    }
  };

  // Helper function to filter processing activities to keep only latest version per processorActivityId
  const filterLatestVersions = (activityList) => {
    if (!activityList || activityList.length === 0) return [];
    
    // Group by processorActivityId
    const grouped = activityList.reduce((acc, item) => {
      const id = item.processorActivityId;
      if (!acc[id]) {
        acc[id] = [];
      }
      acc[id].push(item);
      return acc;
    }, {});
    
    // For each group, keep the one with highest version (and latest updatedAt if versions are equal)
    return Object.values(grouped).map((group) => {
      return group.reduce((latest, current) => {
        if (current.version > latest.version) {
          return current;
        } else if (current.version === latest.version) {
          // If versions are equal, prefer the one with latest updatedAt
          const currentDate = new Date(current.updatedAt);
          const latestDate = new Date(latest.updatedAt);
          return currentDate > latestDate ? current : latest;
        }
        return latest;
      });
    });
  };

  const fetchProcessingActivity = async () => {
    try {
      let res = await dispatch(getProcessingActivity());
      if (res?.status === 200 || res?.status === 201) {
        if (res?.data?.searchList) {
          // Filter to keep only latest versions
          const filteredList = filterLatestVersions(res.data.searchList);
          setProcessingActivityList(filteredList);
          const opts = filteredList.map((item) => ({
            value: item.processorActivityId,
            label: item.activityName,
            processorName: item.processorName,
            dataItems: item.dataTypesList?.flatMap((d) => d.dataItems) || [],
            dataTypesList: item.dataTypesList || [], // Store full dataTypesList structure
          }));
          setProcessingActivityOptions(opts);
          console.log("Processing Activity List:", filteredList);
        } else {
          // Handle case when no processing activity exists - show modal
          setProcessingActivityList([]);
          setProcessingActivityOptions([]);
          setShowNoActivityModal(true);
        }
      }
      if (res?.status === 403) {
        toast.error(
          (props) => (
            <CustomToast
              {...props}
              type="error"
              message={"Facing Network Error..Please try again later."}
            />
          ),
          { icon: false }
        );
      }
    } catch (err) {
      // Better error handling to check if err is an array before accessing
      if (Array.isArray(err) && err[0] && (err[0]?.errorCode == "JCMP4003" || err[0]?.errorCode == "JCMP4001")) {
        toast.error(
          (props) => (
            <CustomToast {...props} type="error" message={"Session expired"} />
          ),
          { icon: false }
        );
      } else {
        // Set empty arrays to prevent undefined errors in UI - show modal
        setProcessingActivityList([]);
        setProcessingActivityOptions([]);
        setShowNoActivityModal(true);
      }
      console.log("Error fetching processing activity:", err);
    }
  };
  useEffect(() => {
    fetchPurposes();
    fetchProcessingActivity();
  }, [dispatch]);

  return (
    <>
      {/* No Processing Activity Modal */}
      {showNoActivityModal && (
        <div className="modal-outer-container">
          <div
            className="master-set-up-modal-container"
            style={{ 
              maxWidth: "450px",
              width: "90%",
              padding: "24px"
            }}
          >
            <div style={{ 
              display: "flex", 
              alignItems: "center", 
              justifyContent: "center",
              gap: "12px",
              marginBottom: "16px"
            }}>
              <Icon
                ic={<IcWarningColored />}
                size="l"
                color="feedback_warning_50"
              />
              <Text appearance="heading-xs" color="primary-grey-100">
                Processing Activity Required
              </Text>
            </div>

            <Text appearance="body-s" color="primary-grey-80" style={{ textAlign: "center", display: "block", marginBottom: "12px" }}>
              No processing activities are available. Processing activities are essential for creating consent templates.
            </Text>

            <Text appearance="body-s" color="primary-grey-80" style={{ textAlign: "center", display: "block", marginBottom: "20px" }}>
              Please navigate to <br></br><span style={{ 
                backgroundColor: "#FFF4E5", 
                padding: "2px 8px", 
                borderRadius: "4px",
                fontWeight: "600",
                color: "#D97706"
              }}>Master Data → Processing Activity</span> <br></br>and create at least one processing activity before proceeding.
            </Text>

            <div style={{ display: "flex", justifyContent: "center" }}>
              <ActionButton
                kind="primary"
                size="medium"
                state="normal"
                label="Go to Master Data"
                onClick={() => navigate('/master')}
              />
            </div>
          </div>
        </div>
      )}

      <div className="aurora-purpose-wrapper">
        {/* Card 1: Purpose & Details */}
        <div className="aurora-card">
          <div className="aurora-card-header">
            <div className="aurora-card-icon-wrapper light-blue">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#0ea5e9" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path></svg>
            </div>
            <div className="aurora-card-header-text">
              <Text appearance="heading-s" color="primary-grey-100">
                Purpose & Details
              </Text>
              <Text appearance="body-xs" color="primary-grey-80">
                Define the key information for this consent template.
              </Text>
            </div>
            <div className="aurora-card-actions">
              {!isFirst && (
                <ActionButton
                  kind="secondary"
                  size="small"
                  state="normal"
                  label="Delete"
                  onClick={onClear}
                />
              )}
            </div>
          </div>
          
          <div className="aurora-card-body">
            <div className="aurora-field-group">
              <Text appearance="body-xs-bold" color="primary-grey-80" className="aurora-field-label">
                Purpose of Consent <span className="aurora-required">*</span>
              </Text>
              <Select
                isMulti
                options={purposeOptions}
                value={purposeOptions.filter((opt) =>
                  data.purposeIds.includes(opt.value)
                )}
                onChange={(selected) => {
                  const updated = selected ? selected.map((s) => s.value) : [];
                  const updatedPNames = selected ? selected.map((s) => s.label) : [];
                  onChange({ purposeIds: updated, purposeNames: updatedPNames });
                }}
                styles={customStyles}
                placeholder="Select purpose"
              />
            </div>

            <div className="aurora-field-row">
              <div className="aurora-field-group half-width">
                <Text appearance="body-xs-bold" color="primary-grey-80" className="aurora-field-label">
                  Is consent mandatory? <span className="aurora-required">*</span>
                </Text>
                <div className="aurora-segmented-control">
                  <label className={`aurora-segment ${data.isMandatory === "Required" ? "active" : ""}`}>
                    <input
                      type="radio"
                      name={`purpose-mandatory-${purposeNumber}`}
                      checked={data.isMandatory === "Required"}
                      onChange={() => onChange({ isMandatory: "Required" })}
                    />
                    <div className="segment-indicator"></div>
                    Required
                  </label>
                  <label className={`aurora-segment ${data.isMandatory === "Optional" ? "active" : ""}`}>
                    <input
                      type="radio"
                      name={`purpose-mandatory-${purposeNumber}`}
                      checked={data.isMandatory === "Optional"}
                      onChange={() => onChange({ isMandatory: "Optional" })}
                    />
                    <div className="segment-indicator"></div>
                    Optional
                  </label>
                </div>
              </div>
            </div>

            <div className="aurora-field-row">
              <div className="aurora-field-group full-width">
                <Text appearance="body-xs-bold" color="primary-grey-80" className="aurora-field-label">
                  Validity Period <span className="aurora-required">*</span>
                </Text>
                <Text appearance="body-xs" color="primary-grey-60" className="aurora-field-subtext">
                  How long will this consent be valid?
                </Text>
                <div className="aurora-validity-group">
                  <div className="aurora-validity-input-wrapper">
                    <input
                      className="aurora-validity-input"
                      value={data.preferenceValidity.value || ""}
                      type="number"
                      placeholder="12"
                      onChange={(e) =>
                        onChange({
                          preferenceValidity: {
                            ...data.preferenceValidity,
                            value: e.target.value,
                          },
                        })
                      }
                    />
                    <div className="aurora-input-arrows">
                       <button className="arrow-up" onClick={(e) => { e.preventDefault(); onChange({ preferenceValidity: { ...data.preferenceValidity, value: (parseInt(data.preferenceValidity.value || 0) + 1).toString() }}) }}>▲</button>
                       <button className="arrow-down" onClick={(e) => { e.preventDefault(); const val = parseInt(data.preferenceValidity.value || 0); if(val > 0) onChange({ preferenceValidity: { ...data.preferenceValidity, value: (val - 1).toString() }}) }}>▼</button>
                    </div>
                  </div>
                  <div className="aurora-validity-select-wrapper">
                    <select
                      className="aurora-validity-select"
                      value={data.preferenceValidity.unit || "MONTHS"}
                      onChange={(e) =>
                        onChange({
                          preferenceValidity: {
                            ...data.preferenceValidity,
                            unit: e.target.value,
                          },
                        })
                      }
                    >
                      <option value="DAYS">Days</option>
                      <option value="MONTHS">Months</option>
                      <option value="YEARS">Years</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>

            <div className="aurora-field-row">
              <div className="aurora-field-group half-width">
                <Text appearance="body-xs-bold" color="primary-grey-80" className="aurora-field-label">
                  Auto-renew after expiry?
                </Text>
                <div className="aurora-segmented-control">
                  <label className={`aurora-segment ${data.autoRenew === "Yes" ? "active" : ""}`}>
                    <input
                      type="radio"
                      name={`purpose-autorenew-${purposeNumber}`}
                      checked={data.autoRenew === "Yes"}
                      onChange={() => onChange({ autoRenew: "Yes" })}
                    />
                    <div className="segment-indicator"></div>
                    Yes
                  </label>
                  <label className={`aurora-segment ${data.autoRenew === "No" ? "active" : ""}`}>
                    <input
                      type="radio"
                      name={`purpose-autorenew-${purposeNumber}`}
                      checked={data.autoRenew === "No"}
                      onChange={() => onChange({ autoRenew: "No" })}
                    />
                    <div className="segment-indicator"></div>
                    No
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Card 2: Processing Information */}
        <div className="aurora-card mt-24">
          <div className="aurora-card-header">
            <div className="aurora-card-icon-wrapper light-blue">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#0ea5e9" strokeWidth="2"><ellipse cx="12" cy="5" rx="9" ry="3"></ellipse><path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3"></path><path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5"></path></svg>
            </div>
            <div className="aurora-card-header-text">
              <Text appearance="heading-s" color="primary-grey-100">
                Processing Information
              </Text>
              <Text appearance="body-xs" color="primary-grey-80">
                Define what data and activities are covered under this consent.
              </Text>
            </div>
          </div>
          
          <div className="aurora-card-body">
            <div className="aurora-field-group">
              <Text appearance="body-xs-bold" color="primary-grey-80" className="aurora-field-label">
                Primary Processing Activity <span className="aurora-required">*</span>
              </Text>
              <Select
                isMulti
                options={processingActivityOption}
                value={processingActivityOption.filter((opt) =>
                  data.purposeActivityIds.includes(opt.value)
                )}
                onChange={(selected) => {
                  const updated = selected ? selected.map((s) => s.value) : [];
                  const updatedPA = selected ? selected.map((s) => s.label) : [];
                  const processorName1 = selected
                    ? selected.map((s) => s.processorName)
                    : [];
                  const processorName = [...new Set(processorName1)];
                  const items1 = selected ? selected.map((s) => s.dataItems) : [];
                  const items = [...new Set(items1)];
                  const dataTypes1 = selected ? selected.map((s) => s.dataTypesList || []) : [];
                  const dataTypes = dataTypes1.filter(dt => dt && dt.length > 0);
                  onChange({
                    purposeActivityIds: updated,
                    usedBy: processorName,
                    dataItems: items,
                    dataTypes: dataTypes,
                    processingAct: updatedPA,
                  });
                }}
                styles={customStyles}
                placeholder="Select processing activity"
              />
            </div>

            <div className="aurora-field-group">
              <Text appearance="body-xs-bold" color="primary-grey-80" className="aurora-field-label">
                Used by
              </Text>
              <div className="aurora-read-only-input">
                {data.usedBy.length ? data.usedBy.join(", ") : "Select activity first"}
              </div>
            </div>

            <div className="aurora-field-group">
              <Text appearance="body-xs-bold" color="primary-grey-80" className="aurora-field-label">
                Related Data Categories
              </Text>
              <div className="aurora-read-only-input">
                {data.dataItems.length ? data.dataItems.join(", ") : "Search or select data categories"}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default PurposeContainer;
